import React from "react";

interface ConfirmModalProps {
  /** Controls modal visibility */
  isOpen: boolean;
  /** Closes the modal */
  onClose: () => void;
  /** Triggered on confirmation */
  onConfirm: () => void;
  /** Loading or processing state */
  loading?: boolean;
  /** Optional title text */
  title?: string;
  /** Optional message body */
  message?: string | React.ReactNode;
  /** Optional extra field for password or passkey confirmation */
  showPasskey?: boolean;
  password?: string;
  setPassword?: (value: string) => void;
  passError?: string;
  /** Custom labels for buttons */
  confirmLabel?: string;
  cancelLabel?: string;
  /** Optional color theme for the confirm button */
  confirmColor?: "red" | "blue" | "green" | "gray";
  /** Optional children override for custom layout */
  children?: React.ReactNode;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  loading = false,
  title = "Confirm Action",
  message = "Are you sure you want to continue?",
  showPasskey = false,
  password = "",
  setPassword,
  passError,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  confirmColor = "blue",
  children,
}) => {
  if (!isOpen) return null;

  const confirmColorClasses: Record<string, string> = {
    red: "bg-red-600 hover:bg-red-700",
    blue: "bg-[#2fa9bf] hover:bg-[#279eb3]",
    green: "bg-green-600 hover:bg-green-700",
    gray: "bg-gray-600 hover:bg-gray-700",
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg p-6 w-80 text-center animate-fadeIn">
        {title && (
          <h3 className="text-lg font-semibold mb-3 text-gray-700">{title}</h3>
        )}

        {/* Allow either message text or full custom content */}
        {children ? (
          children
        ) : (
          <p className="text-sm text-gray-600 mb-4">{message}</p>
        )}

        {showPasskey && (
          <>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword?.(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 mb-3 focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="Enter passkey"
            />
            {passError && (
              <p className="text-red-500 text-sm mb-2">{passError}</p>
            )}
          </>
        )}

        <div className="flex justify-center gap-3 mt-2">
          <button
            onClick={onConfirm}
            disabled={loading}
            className={`${confirmColorClasses[confirmColor]} text-white px-4 py-1 rounded disabled:opacity-50`}
          >
            {loading ? "Processing..." : confirmLabel}
          </button>

          <button
            onClick={onClose}
            disabled={loading}
            className="bg-gray-300 text-gray-800 px-4 py-1 rounded hover:bg-gray-400 disabled:opacity-50"
          >
            {cancelLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmModal;
